import {
	PanelBody,
	SelectControl,
	TextControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';

const SOURCE_OPTIONS = [
	{ label: __( 'Manual', 'brandy-blocks' ), value: 'manual' },
	{ label: __( 'Posts', 'brandy-blocks' ), value: 'posts' },
	{ label: __( 'Products', 'brandy-blocks' ), value: 'products' },
	{ label: __( 'Users', 'brandy-blocks' ), value: 'users' },
	{ label: __( 'Comments', 'brandy-blocks' ), value: 'comments' },
];

const POST_STATUS_OPTIONS = [
	{ label: __( 'Published', 'brandy-blocks' ), value: 'publish' },
	{ label: __( 'Draft', 'brandy-blocks' ), value: 'draft' },
	{ label: __( 'All', 'brandy-blocks' ), value: 'any' },
];

const PRODUCT_FILTER_OPTIONS = [
	{ label: __( 'All products', 'brandy-blocks' ), value: '' },
	{ label: __( 'On sale', 'brandy-blocks' ), value: 'on_sale' },
	{ label: __( 'Featured', 'brandy-blocks' ), value: 'featured' },
];

const PRODUCT_STOCK_OPTIONS = [
	{ label: __( 'Any', 'brandy-blocks' ), value: '' },
	{ label: __( 'In stock', 'brandy-blocks' ), value: 'instock' },
	{ label: __( 'Out of stock', 'brandy-blocks' ), value: 'outofstock' },
	{ label: __( 'On backorder', 'brandy-blocks' ), value: 'onbackorder' },
];

const COMMENT_STATUS_OPTIONS = [
	{ label: __( 'Approved', 'brandy-blocks' ), value: 'approved' },
	{ label: __( 'All', 'brandy-blocks' ), value: 'all' },
];

export default function DataSourceControls( { attributes, setAttributes } ) {
	const {
		source, postType, postStatus, taxonomy, termSlug,
		userRole, commentStatus, productFilter, productStock,
	} = attributes;

	// Fetch available post types.
	const postTypes = useSelect( ( select ) => {
		const types = select( coreStore ).getPostTypes( { per_page: -1 } );
		if ( ! types ) {
			return [];
		}
		return types
			.filter( ( t ) => t.viewable && t.rest_base )
			.map( ( t ) => ( { label: t.labels.singular_name, value: t.slug } ) );
	}, [] );

	// Fetch available taxonomies for the selected post type.
	const taxonomies = useSelect(
		( select ) => {
			if ( source !== 'posts' ) {
				return [];
			}
			const allTaxonomies = select( coreStore ).getTaxonomies( { per_page: -1 } );
			if ( ! allTaxonomies ) {
				return [];
			}
			return allTaxonomies
				.filter( ( t ) => t.types.includes( postType ) )
				.map( ( t ) => ( { label: t.labels.singular_name, value: t.slug } ) );
		},
		[ source, postType ]
	);

	// Fetch product categories for product source.
	const productCategories = useSelect(
		( select ) => {
			if ( source !== 'products' ) {
				return [];
			}
			const terms = select( coreStore ).getEntityRecords( 'taxonomy', 'product_cat', {
				per_page: -1,
				hide_empty: false,
			} );
			if ( ! terms ) {
				return [];
			}
			return terms.map( ( t ) => ( { label: t.name, value: t.slug } ) );
		},
		[ source ]
	);

	const isManual = source === 'manual';

	return (
		<PanelBody
			title={ __( 'Data Source', 'brandy-blocks' ) }
			initialOpen={ ! isManual }
		>
			<SelectControl
				__nextHasNoMarginBottom
				__next40pxDefaultSize
				label={ __( 'Source', 'brandy-blocks' ) }
				value={ source }
				options={ SOURCE_OPTIONS }
				onChange={ ( v ) => setAttributes( { source: v } ) }
			/>

			{ source === 'posts' && (
				<>
					<SelectControl
						__nextHasNoMarginBottom
						__next40pxDefaultSize
						label={ __( 'Post type', 'brandy-blocks' ) }
						value={ postType }
						options={ postTypes }
						onChange={ ( v ) =>
							setAttributes( { postType: v, taxonomy: '', termSlug: '' } )
						}
					/>
					<SelectControl
						__nextHasNoMarginBottom
						__next40pxDefaultSize
						label={ __( 'Status', 'brandy-blocks' ) }
						value={ postStatus }
						options={ POST_STATUS_OPTIONS }
						onChange={ ( v ) => setAttributes( { postStatus: v } ) }
					/>
					{ taxonomies.length > 0 && (
						<>
							<SelectControl
								__nextHasNoMarginBottom
								__next40pxDefaultSize
								label={ __( 'Filter by taxonomy', 'brandy-blocks' ) }
								value={ taxonomy }
								options={ [
									{ label: __( 'None', 'brandy-blocks' ), value: '' },
									...taxonomies,
								] }
								onChange={ ( v ) =>
									setAttributes( { taxonomy: v, termSlug: '' } )
								}
							/>
							{ taxonomy && (
								<TextControl
									__nextHasNoMarginBottom
									label={ __( 'Term slug', 'brandy-blocks' ) }
									value={ termSlug }
									onChange={ ( v ) =>
										setAttributes( { termSlug: v } )
									}
									help={ __(
										'Enter the term slug to filter by',
										'brandy-blocks'
									) }
								/>
							) }
						</>
					) }
				</>
			) }

			{ source === 'products' && (
				<>
					<SelectControl
						__nextHasNoMarginBottom
						__next40pxDefaultSize
						label={ __( 'Filter', 'brandy-blocks' ) }
						value={ productFilter || '' }
						options={ PRODUCT_FILTER_OPTIONS }
						onChange={ ( v ) => setAttributes( { productFilter: v } ) }
					/>
					<SelectControl
						__nextHasNoMarginBottom
						__next40pxDefaultSize
						label={ __( 'Stock status', 'brandy-blocks' ) }
						value={ productStock || '' }
						options={ PRODUCT_STOCK_OPTIONS }
						onChange={ ( v ) => setAttributes( { productStock: v } ) }
					/>
					{ productCategories.length > 0 && (
						<SelectControl
							__nextHasNoMarginBottom
							__next40pxDefaultSize
							label={ __( 'Category', 'brandy-blocks' ) }
							value={ termSlug }
							options={ [
								{ label: __( 'All categories', 'brandy-blocks' ), value: '' },
								...productCategories,
							] }
							onChange={ ( v ) => setAttributes( { termSlug: v } ) }
						/>
					) }
				</>
			) }

			{ source === 'users' && (
				<TextControl
					__nextHasNoMarginBottom
					label={ __( 'Role', 'brandy-blocks' ) }
					value={ userRole }
					onChange={ ( v ) => setAttributes( { userRole: v } ) }
					help={ __(
						'Leave empty for all roles (e.g. subscriber, editor)',
						'brandy-blocks'
					) }
				/>
			) }

			{ source === 'comments' && (
				<SelectControl
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					label={ __( 'Status', 'brandy-blocks' ) }
					value={ commentStatus }
					options={ COMMENT_STATUS_OPTIONS }
					onChange={ ( v ) => setAttributes( { commentStatus: v } ) }
				/>
			) }
		</PanelBody>
	);
}
